import { Controller } from "@hotwired/stimulus"

// Drives the admin Merge tab: pick the surviving company, see exactly what
// would move, confirm with a typed name + password, then poll until the
// background merge finishes.
export default class extends Controller {
  static targets = [
    "search", "results", "retiringName", "typedNameLabel", "submitLabel", "outcome",
    "previewWrap", "previewHeadline", "previewRows", "warnings", "movesHeader", "staysHeader",
    "confirmWrap", "typedName", "password", "error", "submitBtn",
    "progressWrap", "progressText", "progressHint", "spinner", "doneIcon", "failIcon"
  , "untouched", "leftBehind"]

  static values = {
    candidatesUrl: String,
    previewUrl: String,
    mergeUrl: String,
    revertUrl: String,
    statusUrl: String,
    survivorName: String
  }

  connect() {
    this._sourceId = null
    this._csrf = document.querySelector('meta[name="csrf-token"]')?.content
  }

  disconnect() {
    clearTimeout(this._searchTimer)
    clearInterval(this._pollTimer)
  }

  search() {
    clearTimeout(this._searchTimer)
    const q = this.searchTarget.value.trim()
    if (q.length < 2) {
      this.resultsTarget.classList.add("hidden")
      return
    }
    // Say something immediately — the dropdown used to stay blank until
    // results landed (Phelipe 2026-08-28).
    this.resultsTarget.innerHTML =
      `<p class="px-3 py-2 text-[13px] text-gray-400">Searching\u2026</p>`
    this.resultsTarget.classList.remove("hidden")
    this._searchTimer = setTimeout(() => this._fetchCandidates(q), 250)
  }

  async _fetchCandidates(q) {
    const res = await fetch(`${this.candidatesUrlValue}?q=${encodeURIComponent(q)}`, {
      headers: { Accept: "application/json" }
    })
    if (!res.ok) {
      this.resultsTarget.innerHTML =
        `<p class="px-3 py-2 text-[13px] text-gray-400">Couldn't search just now.</p>`
      return
    }
    const rows = await res.json()

    this.resultsTarget.innerHTML = rows.length
      ? rows.map(r => `
          <button type="button" class="w-full text-left px-3 py-2 text-[13px] hover:bg-gray-50 cursor-pointer"
                  data-id="${r.id}" data-name="${this._esc(r.name)}"
                  data-action="click->admin--company-merge#choose">
            ${this._esc(r.name)} <span class="text-gray-400">· ${r.users} ${r.users === 1 ? "person" : "people"}</span>
          </button>`).join("")
      : `<p class="px-3 py-2 text-[13px] text-gray-400">No companies found</p>`
    this.resultsTarget.classList.remove("hidden")
  }

  async choose(event) {
    const btn = event.currentTarget
    this._sourceId = btn.dataset.id
    this._sourceName = btn.dataset.name

    this.resultsTarget.classList.add("hidden")
    this.searchTarget.value = ""
    this.retiringNameTarget.textContent = this._sourceName
    this.retiringNameTarget.classList.add("line-through", "decoration-gray-400")
    this.typedNameLabelTarget.textContent = this._sourceName
    this.submitLabelTarget.textContent = `Merge and retire ${this._sourceName}`
    this.outcomeTarget.textContent =
      `After this, ${this._sourceName} no longer exists and everything lives under ${this.survivorNameValue}.`

    await this._loadPreview()
  }

  // Skeleton rows while the preview is being built. Emptying the tbody left
  // just the headers on screen, which read as "broken" rather than "working"
  // — on a big company the count query takes a moment (Phelipe 2026-08-28).
  _skeletonRows(count = 5) {
    const bar = w => `<div class="h-3 rounded bg-gray-200 animate-pulse" style="width:${w}"></div>`
    return Array.from({ length: count }, () => `
      <tr aria-hidden="true">
        <td class="px-3 py-1.5">${bar("55%")}</td>
        <td class="px-3 py-1.5"><div class="flex justify-end">${bar("36px")}</div></td>
        <td class="px-3 py-1.5"><div class="flex justify-end">${bar("36px")}</div></td>
        <td class="px-3 py-1.5"><div class="flex justify-end">${bar("28px")}</div></td>
        <td class="px-3 py-1.5"><div class="flex justify-end">${bar("44px")}</div></td>
      </tr>`).join("")
  }

  _setPreviewLoading(on, message) {
    if (on) {
      this.previewHeadlineTarget.innerHTML =
        `<span class="inline-flex items-center gap-2 text-gray-500">
           <svg class="w-3.5 h-3.5 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
             <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
             <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
           </svg>${this._esc(message)}</span>`
      this.previewRowsTarget.innerHTML = this._skeletonRows()
      // Don't leave the previous company's "nothing changes" note on screen.
      if (this.hasUntouchedTarget) this.untouchedTarget.classList.add("hidden")
      if (this.hasLeftBehindTarget) this.leftBehindTarget.classList.add("hidden")
    } else {
      this.previewRowsTarget.innerHTML = ""
    }
  }

  async _loadPreview() {
    this._setPreviewLoading(true, "Checking what would move\u2026")
    this.previewWrapTarget.classList.remove("hidden")

    const res = await fetch(`${this.previewUrlValue}?source_id=${this._sourceId}`, {
      headers: { Accept: "application/json" }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      // Clear the skeleton too, or the error reads as still-loading.
      this._setPreviewLoading(false)
      this.previewHeadlineTarget.textContent = data.error || "Could not build the preview."
      return
    }

    const moving = data.total_moving || 0
    const skipped = data.total_skipped || 0
    this.previewHeadlineTarget.textContent =
      `${moving.toLocaleString()} records move into ${this.survivorNameValue}` +
      (skipped ? ` · ${skipped.toLocaleString()} stay behind` : "") +
      (data.total_after ? ` · ${data.total_after.toLocaleString()} there afterwards` : "")

    // Name both companies in the headers so a column can't be misread.
    if (this.hasMovesHeaderTarget) {
      this.movesHeaderTarget.innerHTML =
        `Moves from<br><span class="font-normal text-gray-400">${this._esc(this._sourceName)}</span>`
    }
    if (this.hasStaysHeaderTarget) {
      this.staysHeaderTarget.innerHTML =
        `Stays on<br><span class="font-normal text-gray-400">${this._esc(this._sourceName)}</span>`
    }

    this.previewRowsTarget.innerHTML = (data.tables || []).map(t => `
      <tr>
        <td class="px-3 py-1.5 text-gray-700">${this._esc(this._label(t.table))}</td>
        <td class="px-3 py-1.5 text-right text-gray-700">${t.moving.toLocaleString()}</td>
        <td class="px-3 py-1.5 text-right text-gray-500">${(t.existing || 0).toLocaleString()}</td>
        <td class="px-3 py-1.5 text-right ${t.skipped ? "text-amber-600" : "text-gray-400"}">${
          t.skipped
            ? `<span class="underline decoration-dotted cursor-help" title="${this._esc(this._stayTooltip(t))}">${t.skipped.toLocaleString()}</span>`
            : "—"
        }</td>
        <td class="px-3 py-1.5 text-right font-medium text-gray-900">${(t.after || 0).toLocaleString()}</td>
      </tr>`).join("")

    // Owner-only setup lists deliberately left on the retired company
    // because it never owned a project (Phelipe 2026-08-28).
    const leftBehind = data.left_behind || []
    if (this.hasLeftBehindTarget) {
      if (leftBehind.length) {
        const items = leftBehind
          .map(r => `${this._esc(this._label(r.table))} <span class="text-gray-400">(${r.rows.toLocaleString()})</span>`)
          .join(", ")
        this.leftBehindTarget.innerHTML =
          `<p class="text-[13px] text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
             <span class="font-medium">${this._esc(this._sourceName)} owns no projects, so its setup lists stay put.</span>
             These are the defaults every company starts with — moving them would just duplicate
             ${this._esc(this.survivorNameValue)}'s own lists, so they're left on the retired company:
             <span class="text-blue-700">${items}</span>
           </p>`
        this.leftBehindTarget.classList.remove("hidden")
      } else {
        this.leftBehindTarget.classList.add("hidden")
      }
    }

    // "Nothing changes here" — areas the merge looked at and found empty.
    const untouched = data.untouched || []
    if (this.hasUntouchedTarget) {
      if (untouched.length) {
        const names = untouched.map(t => this._esc(this._label(t))).join(", ")
        this.untouchedTarget.innerHTML =
          `<p class="text-[13px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
             <span class="font-medium text-gray-600">Nothing changes in ${untouched.length} area${untouched.length === 1 ? "" : "s"}</span>
             — ${this._esc(this._sourceName)} has no records there, so nothing moves and nothing is touched on ${this._esc(this.survivorNameValue)}:
             <span class="text-gray-400">${names}</span>
           </p>`
        this.untouchedTarget.classList.remove("hidden")
      } else {
        this.untouchedTarget.classList.add("hidden")
      }
    }

    const warnings = data.warnings || []
    if (warnings.length) {
      this.warningsTarget.innerHTML = warnings.map(w => `
        <p class="text-[13px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">${this._esc(w)}</p>`).join("")
      this.warningsTarget.classList.remove("hidden")
    } else {
      this.warningsTarget.classList.add("hidden")
    }

    this.confirmWrapTarget.classList.remove("hidden")
  }

  async submit() {
    this._setError(null)
    if (this.typedNameTarget.value.trim() !== (this._sourceName || "").trim()) {
      this._setError("Type the company name exactly as shown.")
      return
    }
    if (!this.passwordTarget.value) {
      this._setError("Enter your password.")
      return
    }

    this.submitBtnTarget.disabled = true
    const body = new FormData()
    body.append("source_id", this._sourceId)
    body.append("typed_name", this.typedNameTarget.value)
    body.append("password", this.passwordTarget.value)

    const res = await fetch(this.mergeUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": this._csrf, Accept: "application/json" },
      body
    })
    const data = await res.json().catch(() => ({}))
    this.submitBtnTarget.disabled = false

    if (!res.ok) {
      this._setError(data.error || "Could not start the merge.")
      return
    }

    this.confirmWrapTarget.classList.add("hidden")
    this._startProgress(`Merging ${this._sourceName} into ${this.survivorNameValue}…`, "Large companies can take a few minutes. You can leave this page — it keeps running.")
    this._poll(data.merge_id)
  }

  async revert(event) {
    const mergeId = event.currentTarget.dataset.mergeId
    const password = window.prompt("Enter your password to undo this merge:")
    if (!password) return

    const body = new FormData()
    body.append("merge_id", mergeId)
    body.append("password", password)

    const res = await fetch(this.revertUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": this._csrf, Accept: "application/json" },
      body
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      window.alert(data.error || "Could not undo this merge.")
      return
    }

    this._startProgress("Undoing the merge…", "Putting every moved record back where it was.")
    this._poll(mergeId)
  }

  _poll(mergeId) {
    clearInterval(this._pollTimer)
    this._pollTimer = setInterval(async () => {
      const res = await fetch(`${this.statusUrlValue}?merge_id=${mergeId}`, {
        headers: { Accept: "application/json" }
      })
      if (!res.ok) return
      const data = await res.json()

      if (data.status === "completed") {
        clearInterval(this._pollTimer)
        const stayed = data.skipped ? `, ${data.skipped.toLocaleString()} stayed behind` : ""
        this._finishProgress(
          `Merge complete — ${(data.moved || 0).toLocaleString()} records moved${stayed}.`,
          "Refreshing the page…"
        )
        // This page belongs to the surviving company, so a plain refresh
        // shows the new totals and the undo entry.
        setTimeout(() => window.location.reload(), 2500)
      } else if (data.status === "reverted") {
        clearInterval(this._pollTimer)
        this._finishProgress("Merge undone — everything is back where it was.", "Refreshing the page…")
        setTimeout(() => window.location.reload(), 2500)
      } else if (data.status === "failed") {
        clearInterval(this._pollTimer)
        this._failProgress(data.error || "The merge stopped before finishing.")
      } else {
        const secs = Math.round((Date.now() - this._startedAt) / 1000)
        if (this.hasProgressHintTarget && secs > 5) {
          this.progressHintTarget.textContent = `Still working — ${secs}s elapsed.`
          this.progressHintTarget.classList.remove("hidden")
        }
      }
    }, 3000)
  }

  // Full list of what stays behind on this row, grouped by why.
  _stayTooltip(row) {
    const stays = row.stays || []
    if (!stays.length) return `${row.skipped} stay on ${this._sourceName}`

    const reasons = {
      identical: `${this.survivorNameValue} already has an identical one`,
      unsafe_key: "Name clash on a code that cannot be renamed safely"
    }
    const groups = {}
    stays.forEach(s => { (groups[s.reason] ||= []).push(s.label) })

    const lines = [`${row.skipped} stay on ${this._sourceName}:`]
    Object.entries(groups).forEach(([reason, labels]) => {
      lines.push("")
      lines.push(`${reasons[reason] || reason} (${labels.length}):`)
      labels.forEach(l => lines.push(`  • ${l}`))
    })
    const shown = stays.length
    if (row.skipped > shown) lines.push("", `…and ${(row.skipped - shown).toLocaleString()} more`)
    return lines.join("\n")
  }

  _startProgress(text, hint) {
    this._startedAt = Date.now()
    this.progressWrapTarget.classList.remove("hidden")
    this.spinnerTarget.classList.remove("hidden")
    this.doneIconTarget.classList.add("hidden")
    this.failIconTarget.classList.add("hidden")
    this.progressTextTarget.textContent = text
    this.progressTextTarget.className = "text-[13px] text-text-body"
    if (hint) {
      this.progressHintTarget.textContent = hint
      this.progressHintTarget.classList.remove("hidden")
    }
  }

  _finishProgress(text, hint) {
    this.spinnerTarget.classList.add("hidden")
    this.doneIconTarget.classList.remove("hidden")
    this.progressTextTarget.textContent = text
    this.progressTextTarget.className = "text-[13px] font-medium text-green-700"
    this.progressHintTarget.textContent = hint || ""
    this.progressHintTarget.classList.toggle("hidden", !hint)
  }

  _failProgress(message) {
    this.spinnerTarget.classList.add("hidden")
    this.failIconTarget.classList.remove("hidden")
    this.progressTextTarget.textContent = message
    this.progressTextTarget.className = "text-[13px] font-medium text-red-700"
    this.progressHintTarget.textContent = "Nothing else was changed. You can undo this merge from the history below."
    this.progressHintTarget.classList.remove("hidden")
  }

  _label(table) {
    return table.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
  }

  _setError(msg) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = msg || ""
    this.errorTarget.classList.toggle("hidden", !msg)
  }

  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str == null ? "" : String(str)
    return d.innerHTML
  }
}
